import React, { useState, useRef, useEffect } from "react";
import { useDeal } from "../lib/store";
import { streamPost, emailApi, dealsApi, lendersApi, contactApi } from "../lib/api";
import { BULK_LIMIT, BulkSelectBar } from "../components/BulkSelectBar";
import { SendToFolkModal } from "../components/SendToFolkModal";
import { Card, Tile, ResearchModal, useResearch, AddToCampaignModal, useOutreach, ComposeEmailModal, useCompose, SiteLink } from "../components/shared";
import * as m from "../lib/model";
import { toast } from "sonner";
import { Upload, Globe, Trash2, Search, Share2, UserSearch, Users2 } from "lucide-react";

const parseEmail = (text) => {
  const mt = text.match(/subject:\s*(.*)/i);
  const subject = mt ? mt[1].trim() : "Financing request — XYZ Ventures acquisition";
  const body = text.replace(/subject:.*(\r?\n)?/i, "").trim();
  return { subject, body };
};

const COLLATERAL_OPTIONS = [
  "Business assets & goodwill",
  "Enterprise cash flow",
  "Owner-occupied real estate",
  "Equipment & vehicles (FF&E)",
  "Accounts receivable",
  "Inventory",
  "Blanket UCC-1 lien",
  "SBA personal guarantee",
  "Assignment of key contracts",
  "Key-person life insurance",
];

export default function Lenders() {
  const { M, setField, lenders, setLenders, dealId, industry, setIndustry, findingLenders, runLenders, findMoreLenders } = useDeal();
  const [typeFilter, setTypeFilter] = useState("All");
  const [selected, setSelected] = useState({});
  const [drafts, setDrafts] = useState({});

  const [crm, setCrm] = useState([]);
  const [crmStats, setCrmStats] = useState({ total: 0, byType: {} });
  const [crmQ, setCrmQ] = useState("");
  const [crmType, setCrmType] = useState("All");
  const crmFileRef = useRef(null);
  const [crmLimit, setCrmLimit] = useState(500);
  const [loadingAllCrm, setLoadingAllCrm] = useState(false);
  const [folkOpen, setFolkOpen] = useState(false);
  const loadCrm = () => lendersApi.list({ q: crmQ || undefined, type: crmType, limit: crmLimit }).then(setCrm).catch(() => {});
  useEffect(() => { lendersApi.stats().then(setCrmStats).catch(() => {}); }, [crm.length]);
  useEffect(() => { const t = setTimeout(loadCrm, 250); return () => clearTimeout(t); }, [crmQ, crmType]);
  const delCrm = async (id) => { await lendersApi.remove(id); loadCrm(); };
  const [findingId, setFindingId] = useState(null);
  const findContactLender = async (l) => {
    setFindingId(l.id);
    const tid = toast.loading(`Finding a contact at ${l.company || l.name}…`);
    try {
      const r = await contactApi.find({ company: l.company || l.name, website: l.domain, role_hint: "commercial lending / SBA / credit officer or managing director" });
      if (!r.found) { toast.error("No contact found — try the lender's website", { id: tid }); return; }
      await lendersApi.update(l.id, { name: l.name || r.name, title: l.title || r.title, email: l.email || r.email, phone: l.phone || r.phone, notes: [l.notes, r.name && `Contact: ${r.name}${r.title ? `, ${r.title}` : ""}${r.email ? ` (${r.email})` : ""}`, r.note].filter(Boolean).join(" · ").slice(0, 480) });
      toast.success(`${r.name || "Contact"}${r.title ? `, ${r.title}` : ""}${r.email ? ` · ${r.email}` : ""}${r.source ? ` (${r.source})` : ""}`, { id: tid });
      loadCrm();
    } catch (e) { toast.error(e?.response?.data?.detail || "Contact lookup failed", { id: tid }); }
    finally { setFindingId(null); }
  };
  const [crmSel, setCrmSel] = useState({});
  const crmSelCount = Object.values(crmSel).filter(Boolean).length;
  const toggleCrm = (id) => setCrmSel((s) => ({ ...s, [id]: !s[id] }));
  const selectAllCrm = () => { const n = {}; crm.forEach((l) => { n[l.id] = true; }); setCrmSel(n); };
  // The table pages at 500 rows — pull the rest of the filtered set in, then tick everything.
  const selectAllMatchingCrm = async () => {
    setLoadingAllCrm(true);
    try {
      const rows = await lendersApi.list({ q: crmQ || undefined, type: crmType, limit: BULK_LIMIT });
      setCrm(rows); setCrmLimit(BULK_LIMIT);
      const n = {}; rows.forEach((l) => { n[l.id] = true; }); setCrmSel(n);
      const withEmail = rows.filter((l) => l.email).length;
      toast.success(`${rows.length.toLocaleString()} lenders selected · ${withEmail.toLocaleString()} with an email`);
      if (rows.length >= BULK_LIMIT) toast.info(`That is the first ${BULK_LIMIT.toLocaleString()} matching — narrow the search or type filter to reach the rest`);
      if (withEmail > 1000) toast.info("Instantly takes 1,000 leads per add — narrow the filter and repeat for the rest");
    } catch { toast.error("Could not load the full list"); }
    finally { setLoadingAllCrm(false); }
  };
  const toggleAllCrm = () => { const all = crm.length && crm.every((l) => crmSel[l.id]); const n = {}; if (!all) crm.forEach((l) => { n[l.id] = true; }); setCrmSel(n); };
  const onCrmFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      const delim = lines[0].includes(";") ? ";" : ",";
      const hdr = lines[0].toLowerCase().split(delim).map((h) => h.trim());
      const idx = (k) => hdr.findIndex((h) => h.includes(k));
      const iFn = idx("firstname"), iLn = idx("lastname"), iName = idx("name"), iEmail = idx("email"), iTitle = idx("title") >= 0 ? idx("title") : idx("job"), iCo = idx("company"), iDom = idx("domain"), iPh = idx("phone"), iGr = idx("group");
      const items = lines.slice(1).map((l) => {
        const c = l.split(delim).map((x) => x.trim().replace(/^"|"$/g, ""));
        const nm = iFn >= 0 ? `${c[iFn] || ""} ${iLn >= 0 ? c[iLn] || "" : ""}`.trim() : (iName >= 0 ? c[iName] : c[0]);
        return { name: nm, email: iEmail >= 0 ? (c[iEmail] || "").split(",")[0] : "", title: iTitle >= 0 ? c[iTitle] : "", company: iCo >= 0 ? c[iCo] : "", domain: iDom >= 0 ? c[iDom] : "", phone: iPh >= 0 ? (c[iPh] || "").split(",")[0] : "", tags: iGr >= 0 ? c[iGr] : "", type: "Bank" };
      }).filter((x) => x.name);
      const res = await lendersApi.bulk(items);
      toast.success(`Imported ${res.added} lender(s)`);
      loadCrm();
    } catch { toast.error("Could not parse CSV"); }
    finally { if (crmFileRef.current) crmFileRef.current.value = ""; }
  };
  const [draftingIdx, setDraftingIdx] = useState(null);
  const [sending, setSending] = useState(false);
  const [collat, setCollat] = useState(() => ({
    "Business assets & goodwill": true,
    "Enterprise cash flow": true,
    "Owner-occupied real estate": !!M.slbOn,
    "Equipment & vehicles (FF&E)": true,
    "Accounts receivable": true,
    "Inventory": true,
    "Blanket UCC-1 lien": true,
    "SBA personal guarantee": true,
    "Assignment of key contracts": false,
    "Key-person life insurance": false,
  }));
  const fileRef = useRef(null);

  const seniorLoan = m.loanAmt(M);
  const dscr = m.dscrYr1(M);
  const equity = m.exitReturns(M).equityInvested;
  const solver = m.maxLoanAtDSCR(M, 1.25);
  const collateral = COLLATERAL_OPTIONS.filter((c) => collat[c]);



  const doFindLenders = async () => { setSelected({}); setDrafts({}); await runLenders(); };

  const research = useResearch("lenders");
  const outreach = useOutreach();
  const compose = useCompose();
  const lenderVars = () => [["Deal name", M.dealName], ["Senior debt", m.fmt$(seniorLoan)], ["Purchase price", m.fmt$(M.askPrice)], ["Adj. SDE", m.fmt$(M.sde)], ["Year-1 DSCR", m.fmtX(dscr)]];
  const openComposeCrm = () => {
    const picked = crm.filter((l) => crmSel[l.id]);
    if (!picked.length) return toast.error("Select at least one lender");
    compose.start({
      title: "Email Lenders",
      recipients: picked.map((l) => ({ name: l.name, email: l.email })),
      subject: `Financing request — ${M.dealName}`,
      body: defaultBody(),
      kind: "lenders",
      dealName: M.dealName,
      dealKey: M.dealKey || dealId || "",
      vars: lenderVars(),
      draftEndpoint: "/ai/lender-outreach",
      draftPayload: { deal: { ...M, industry }, lender: { ...(picked[0] || {}), contact: picked.length === 1 ? ((picked[0] || {}).contact || (picked[0] || {}).name || "") : "{{firstName}}" } },
    });
  };
  const openComposeFinder = () => {
    const picked = lenders.filter((_, i) => selected[i]);
    if (!picked.length) return toast.error("Select at least one lender");
    compose.start({
      title: "Email Lenders",
      recipients: picked.map((l) => ({ name: l.name, email: l.email })),
      subject: `Financing request — ${M.dealName}`,
      body: defaultBody(),
      kind: "lenders",
      dealName: M.dealName,
      dealKey: M.dealKey || dealId || "",
      vars: lenderVars(),
      draftEndpoint: "/ai/lender-outreach",
      draftPayload: { deal: { ...M, industry }, lender: { ...(picked[0] || {}), contact: picked.length === 1 ? ((picked[0] || {}).contact || (picked[0] || {}).name || "") : "{{firstName}}" } },
    });
  };
  const addCrmToCampaign = () => {
    const list = crm.filter((l) => crmSel[l.id]).map((l) => ({ name: l.name, email: l.email, company: l.company, title: l.title, phone: l.phone, website: l.domain, notes: l.tags }));
    if (!list.length) return toast.error("Select at least one lender");
    outreach.start(list, "XYZ Lenders");
  };
  const S = (v) => (Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v));
  const saveLender = async (e) => {
    setLenders([{ name: S(e.name), type: S(e.type) || "Lender", email: S(e.email), loanRange: S(e.loanRange), rateGuide: S(e.rateGuide), geography: S(e.geography), website: S(e.website), collateral: "", fitScore: 75 }, ...lenders]);
    toast.success(`Added ${e.name} to lender shortlist`);
  };
  const describeLender = (e) => (
    <div style={{ fontSize: 12 }}>
      <div><b>{e.name}</b>{e.type ? <span className="tag" style={{ marginLeft: 6, fontSize: 9 }}>{e.type}</span> : null}</div>
      <div style={{ color: "var(--text3)", marginTop: 2, fontSize: 11 }}>{[e.loanRange && `Loans ${e.loanRange}`, e.rateGuide && `Rate ${e.rateGuide}`, e.geography].filter(Boolean).join(" · ")}</div>
      {e.website && <a href={/^https?:/.test(e.website) ? e.website : `https://${e.website}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--lime)" }}>{e.website}</a>}
    </div>
  );

  const autoSize = () => {
    const target = solver.maxSeniorLoan;
    const dp = M.askPrice > 0 ? Math.min(100, Math.max(0, (1 - (target + (M.snAmt || 0)) / M.askPrice) * 100)) : M.downPct;
    setField("downPct", Number(dp.toFixed(1)));
    toast.success(`Sized senior loan to ${m.fmt$(target)} at 1.25× DSCR`);
  };

  const draftEmail = async (l, i) => {
    setDraftingIdx(i);
    try {
      await streamPost("/ai/lender-outreach", { deal: { ...M, industry }, lender: l }, (c, f) => setDrafts((d) => ({ ...d, [i]: f })));
    } catch (e) { toast.error("Draft failed"); }
    finally { setDraftingIdx(null); }
  };

  const sendOne = (l, i) => {
    const draft = drafts[i] ? parseEmail(drafts[i]) : null;
    compose.start({
      title: `Email ${l.name}`,
      recipients: [{ name: l.name, email: l.email }],
      subject: draft?.subject || `Financing request — ${M.dealName}`,
      body: draft?.body || defaultBody(),
      kind: "lenders",
      dealName: M.dealName,
      dealKey: M.dealKey || dealId || "",
      vars: lenderVars(),
      draftEndpoint: "/ai/lender-outreach",
      draftPayload: { deal: { ...M, industry }, lender: { ...l, contact: l.contact || l.name || "" } },
    });
  };

  const sendSelected = async () => {
    const recips = lenders.filter((_, i) => selected[i]).filter((l) => l.email).map((l) => ({ name: l.name, email: l.email }));
    if (!recips.length) return toast.error("Select at least one lender with an email");
    setSending(true);
    try {
      const res = await emailApi.sendDirect({ recipients: recips, subject: `Financing request — ${M.dealName}`, body: defaultBody(), deal_name: M.dealName, kind: "lenders" });
      if (res.mock) toast.warning(`Recorded ${res.sent} recipient(s) — no live email`);
      else toast.success(`Financing request delivered to ${res.delivered}/${res.sent} lender(s) via Resend`);
      setSelected({});
    } finally { setSending(false); }
  };

  const defaultBody = () =>
    `Hi {{firstName}},\n\nXYZ Ventures is acquiring an established ${industry.toLowerCase()} business in the DFW Metroplex and is sourcing senior debt.\n\n` +
    `• Senior debt requested: ${m.fmt$(seniorLoan)}\n• Purchase price: ${m.fmt$(M.askPrice)}\n• Adjusted EBITDA/SDE: ${m.fmt$(M.sde)}\n` +
    `• Year-1 DSCR: ${m.fmtX(dscr)}\n• Equity injection: ${m.fmt$(equity)}\n• Collateral: ${collateral.join(", ")}\n\n` +
    `We'd welcome your appetite and indicative terms. Full package available under NDA.\n\nBest regards,\nXYZ Ventures`;

  const saveShortlist = async () => {
    if (!dealId) return toast.error("Save this deal in the Pipeline first, then attach lenders");
    await dealsApi.update(dealId, { lenderProspects: lenders });
    toast.success(`Attached ${lenders.length} lenders to this deal`);
  };

  const uploadList = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const lines = reader.result.split(/\r?\n/).filter(Boolean);
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const rows = lines.slice(1).map((ln) => {
        const cells = ln.split(",");
        const o = {}; headers.forEach((h, i) => (o[h] = (cells[i] || "").trim()));
        return { name: o.name || o.lender || "", type: o.type || "Uploaded", email: o.email || "", loanRange: o.loanrange || o["loan range"] || "", rateGuide: o.rateguide || o.rate || "", collateral: o.collateral || "", geography: o.geography || "", fitScore: Number(o.fitscore) || 70 };
      }).filter((l) => l.name);
      setLenders([...rows, ...lenders]);
      toast.success(`Added ${rows.length} lenders from CSV`);
    };
    reader.readAsText(file);
  };

  const types = ["All", ...Array.from(new Set(lenders.map((l) => l.type).filter(Boolean)))];
  const shown = lenders.map((l, i) => ({ l, i })).filter(({ l }) => typeFilter === "All" || l.type === typeFilter);
  const selCount = Object.values(selected).filter(Boolean).length;
  const fitTone = (s) => (s >= 75 ? "go" : s >= 50 ? "amber" : "pass");

  return (
    <div className="page" data-testid="page-lenders">
      <div className="page-head"><div className="page-title">Debt Lenders</div><div className="page-sub">Source senior &amp; subordinated debt — AI-matched financing partners; attach the credit package from the Docs Room</div></div>

      <Card title="Debt Capacity Check (internal)" testid="debt-solver" right={<button className="btn" onClick={autoSize} data-testid="auto-size">Auto-size @1.25× DSCR</button>}>
        <div className="grid g4">
          <Tile label="Max senior loan @ 1.25× DSCR" value={m.fmt$(solver.maxSeniorLoan)} testid="solver-max" />
          <Tile label="Requested senior loan" value={m.fmt$(solver.requested)} testid="solver-req" />
          <Tile label="Debt-capacity headroom" value={`${solver.headroom >= 0 ? "+" : ""}${m.fmt$(solver.headroom)}`} tone={solver.fits ? "go" : "pass"} testid="solver-headroom" />
          <Tile label="Verdict" value={solver.fits ? "Fits the box ✓" : "Over-levered"} tone={solver.fits ? "go" : "pass"} small testid="solver-verdict" />
        </div>
      </Card>

      <Card title="Collateral Offered to Lenders" style={{ marginTop: 12 }} testid="lender-collateral">
        <div className="ir-fine" style={{ marginBottom: 8 }}>Ticked items are listed in every financing email. Build the full lender package in the Teaser Generator and attach it (or any tax return / financials) from the Docs Room.</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }} data-testid="collateral-picker">
          {COLLATERAL_OPTIONS.map((c) => (
            <label key={c} style={{ display: "flex", gap: 6, fontSize: 11.5, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={!!collat[c]} onChange={(e) => setCollat((s) => ({ ...s, [c]: e.target.checked }))} data-testid={`collat-${c}`} /> {c}
            </label>
          ))}
        </div>
      </Card>

      <Card title="AI Lender Finder" style={{ marginTop: 12 }} testid="lender-finder" right={(
        <span style={{ display: "flex", gap: 6 }}>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={(e) => e.target.files[0] && uploadList(e.target.files[0])} data-testid="lender-csv-input" />
          <button className="btn" onClick={() => fileRef.current?.click()} data-testid="lender-upload"><Upload size={13} style={{ marginRight: 4 }} />Upload List</button>
          {lenders.length > 0 && <button className="btn" onClick={findMoreLenders} disabled={findingLenders} data-testid="find-more-lenders">{findingLenders ? "…" : "Find More"}</button>}
          <button className="btn" onClick={() => research.start(`${industry || M.companyName || "small business"} acquisition financing`, "United States")} data-testid="find-lenders-web"><Globe size={13} />Find lenders (web)</button>
          <button className="btn btn-primary" onClick={doFindLenders} disabled={findingLenders} data-testid="find-lenders">{findingLenders ? "Searching…" : lenders.length ? "Restart Search" : "Find Matching Lenders"}</button>
        </span>
      )}>
        <div className="field" style={{ maxWidth: 360 }}><label>Industry (for lender targeting)</label><input className="inp" value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="lender-industry" /></div>
        {findingLenders && <div className="bullet">Scanning nationwide across multiple passes — SBA 7(a), SBICs, community &amp; regional banks, credit unions and private-credit funds… {lenders.length > 0 ? `${lenders.length} found so far` : ""}</div>}
        {lenders.length > 0 && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "10px 0" }}>
            <select className="inp" style={{ width: 220, padding: "6px 8px", fontSize: 12 }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="lender-type-filter">
              {types.map((t) => <option key={t} value={t}>{t === "All" ? "All lender types" : t}</option>)}
            </select>
            <span style={{ fontSize: 11.5, color: "var(--text3)" }}>{shown.length} shown · {selCount} selected</span>
            <div style={{ flex: 1 }} />
            <button className="btn" onClick={saveShortlist} data-testid="save-lenders">Save Shortlist To Deal</button>
            <button className="btn btn-primary" onClick={openComposeFinder} disabled={!selCount} data-testid="send-lenders">Email ({selCount})</button>
          </div>
        )}
        <div className="grid g2">
          {shown.map(({ l, i }) => (
            <div key={i} className="lender-card" data-testid="lender-card" style={{ background: "var(--bg2)", border: `1px solid ${selected[i] ? "var(--lime)" : "var(--border)"}`, borderRadius: 3, padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <label style={{ display: "flex", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!selected[i]} onChange={(e) => setSelected((s) => ({ ...s, [i]: e.target.checked }))} data-testid={`lender-select-${i}`} />
                  <div><div style={{ fontWeight: 700, fontSize: 14 }}>{l.name}</div><div style={{ fontSize: 11.5, color: "var(--text2)" }}>{l.type} · {l.geography}</div></div>
                </label>
                <span className={`pill ${fitTone(l.fitScore)}`} style={{ whiteSpace: "nowrap" }}>{l.fitScore} fit</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 10px", fontSize: 11.5, marginTop: 10 }}>
                {l.loanRange ? <><span style={{ color: "var(--text3)" }}>Loan range</span><span>{l.loanRange}</span></> : null}
                {l.rateGuide ? <><span style={{ color: "var(--text3)" }}>Rate guide</span><span>{l.rateGuide}</span></> : null}
                {l.maxLeverage ? <><span style={{ color: "var(--text3)" }}>Leverage / DSCR</span><span>{l.maxLeverage}</span></> : null}
                {l.collateral ? <><span style={{ color: "var(--text3)" }}>Collateral</span><span>{l.collateral}</span></> : null}
                {l.recentDeal ? <><span style={{ color: "var(--text3)" }}>Recent deal</span><span>{l.recentDeal}</span></> : null}
              </div>
              {l.fitRationale ? <div style={{ fontSize: 11.5, color: "var(--text2)", marginTop: 8, fontStyle: "italic" }}>{l.fitRationale}</div> : null}
              {(l.contactName || l.email) ? (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)", fontSize: 11.5 }}>
                  <b>{l.contactName}</b>{l.contactTitle ? ` · ${l.contactTitle}` : ""}<br /><span style={{ color: "var(--lime)" }}>{l.email}</span>
                </div>
              ) : null}
              {drafts[i] ? <div style={{ marginTop: 8, background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 3, padding: 10, fontSize: 11.5, whiteSpace: "pre-wrap", maxHeight: 200, overflow: "auto" }} data-testid={`lender-draft-${i}`}>{drafts[i]}</div> : null}
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <button className="btn" onClick={() => draftEmail(l, i)} disabled={draftingIdx === i} data-testid={`draft-lender-${i}`}>{draftingIdx === i ? "Drafting…" : drafts[i] ? "Redraft" : "Draft Tailored Email"}</button>
                <button className="btn btn-primary" onClick={() => sendOne(l, i)} data-testid={`send-lender-${i}`}>Compose Email</button>
              </div>
            </div>
          ))}
        </div>
        {!findingLenders && lenders.length === 0 && <div className="bullet">Click “Find Matching Lenders” to source financing partners — or “Upload List” to bring your own lender CSV (columns: name, type, email, loanRange, rateGuide, collateral, geography).</div>}
      </Card>

      {lenders.length > 0 && (
        <Card title={`Term Sheet Comparison${selCount ? ` — ${selCount} selected` : ""}`} className="table-scroll" style={{ marginTop: 12 }} testid="termsheet-compare">
          <table className="tbl">
            <thead><tr><th>Lender</th><th>Type</th><th className="num">Loan range</th><th className="num">Rate</th><th className="num">Max leverage</th><th className="num">Fees</th><th className="num">Fit</th></tr></thead>
            <tbody>
              {(selCount ? lenders.filter((_, i) => selected[i]) : lenders).map((l, i) => (
                <tr key={i} data-testid="compare-row">
                  <td><b>{l.name}</b><SiteLink url={l.website} name={l.name} testid={`lender-site-${i}`} /></td>
                  <td style={{ color: "var(--text2)" }}>{l.type}</td>
                  <td className="num">{l.loanRange || "—"}</td>
                  <td className="num">{l.rateGuide || "—"}</td>
                  <td className="num">{l.maxLeverage || "—"}</td>
                  <td className="num">{l.fees || "—"}</td>
                  <td className="num"><span className={`pill ${fitTone(l.fitScore)}`}>{l.fitScore}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 8 }}>Select lenders above to compare a shortlist, or leave unselected to compare all found lenders.</div>
        </Card>
      )}

      <Card title={`Debt Lender CRM — ${crmStats.total.toLocaleString()} contacts`} className="table-scroll" style={{ marginTop: 12 }} testid="lender-crm"
        right={<div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: 8, top: 9, color: "var(--text3)" }} />
            <input className="inp" style={{ paddingLeft: 26, width: 190 }} placeholder="Search name, company, email…" value={crmQ} onChange={(e) => setCrmQ(e.target.value)} data-testid="lender-crm-search" />
          </div>
          <select className="inp" value={crmType} onChange={(e) => setCrmType(e.target.value)} data-testid="lender-crm-type">
            <option>All</option>{Object.keys(crmStats.byType || {}).map((t) => <option key={t}>{t}</option>)}
          </select>
          <button className="btn" onClick={() => research.start(`${industry || M.companyName || "small business"} acquisition financing`, "United States")} data-testid="lender-crm-ai"><Globe size={13} />Find matching lenders (AI)</button>
          <button className="btn" onClick={() => crmFileRef.current?.click()} data-testid="lender-crm-upload"><Upload size={13} />Upload CSV</button>
          <input ref={crmFileRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={onCrmFile} data-testid="lender-crm-file" />
        </div>}>
        <BulkSelectBar count={crmSelCount} total={crm.length} withEmail={crm.filter((l) => crmSel[l.id] && l.email).length}
          noun="lenders" allSelected={crm.length > 0 && crmSelCount >= crm.length} onSelectAll={selectAllCrm} onClear={() => setCrmSel({})} testid="lender-bulk"
          onSelectMatching={crm.length >= crmLimit ? selectAllMatchingCrm : undefined} matchingBusy={loadingAllCrm}>
          <button className="btn btn-primary" onClick={openComposeCrm} disabled={crmSelCount === 0} data-testid="lender-crm-send">Email ({crmSelCount})</button>
          <button className={`btn ${crmSelCount ? "btn-primary" : ""}`} onClick={addCrmToCampaign} disabled={crmSelCount === 0} data-testid="lender-crm-campaign"><Share2 size={13} style={{ marginRight: 4 }} />Add to campaign ({crmSelCount})</button>
          <button className="btn" onClick={() => setFolkOpen(true)} disabled={crmSelCount === 0} data-testid="lender-folk-send"><Users2 size={13} style={{ marginRight: 4 }} />Send to folk</button>
        </BulkSelectBar>
        <table className="tbl" style={{ minWidth: 860 }} data-testid="lender-crm-table">
          <thead><tr><th style={{ width: 28 }}><input type="checkbox" checked={crm.length > 0 && crm.every((l) => crmSel[l.id])} onChange={toggleAllCrm} data-testid="lender-crm-all" /></th><th>Name</th><th>Title</th><th>Company</th><th>Email</th><th>Phone</th><th>Type</th><th>Tags</th><th></th></tr></thead>
          <tbody>
            {crm.length === 0 ? <tr><td colSpan={9} style={{ color: "var(--text3)", padding: 12 }}>No lenders found. Adjust search or upload a CSV.</td></tr> :
              crm.map((l) => (
                <tr key={l.id} data-testid={`lender-crm-row-${l.id}`}>
                  <td><input type="checkbox" checked={!!crmSel[l.id]} onChange={() => toggleCrm(l.id)} data-testid={`lender-crm-check-${l.id}`} /></td>
                  <td>{l.name}</td>
                  <td style={{ fontSize: 11, color: "var(--text2)" }}>{l.title}</td>
                  <td style={{ fontSize: 11 }}>{l.company}<SiteLink url={l.domain} name={l.company} testid={`lender-crm-site-${l.id}`} />{l.domain ? <div style={{ color: "var(--text3)" }}>{l.domain}</div> : null}</td>
                  <td style={{ fontSize: 11 }}>{l.email}</td>
                  <td style={{ fontSize: 11 }}>{l.phone}</td>
                  <td><span className="tag" style={{ fontSize: 9 }}>{l.type}</span></td>
                  <td style={{ fontSize: 10.5, color: "var(--text3)" }}>{l.tags}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn" style={{ padding: "3px 7px", marginRight: 4 }} onClick={() => findContactLender(l)} disabled={findingId === l.id} title="Find a verified contact (Apollo + website/team)" data-testid={`lender-crm-find-${l.id}`}><UserSearch size={12} style={{ marginRight: 3 }} />{findingId === l.id ? "…" : "Find contact"}</button>
                    <button className="btn" style={{ padding: "3px 6px" }} onClick={() => delCrm(l.id)} data-testid={`lender-crm-del-${l.id}`}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {crm.length >= crmLimit && <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 8 }}>Showing the first {crmLimit.toLocaleString()} — refine with search or the type filter, or use “Select more lenders matching this filter” above.</div>}
      </Card>

      <ResearchModal open={research.open} onClose={() => research.setOpen(false)} title="Find lenders — live web search"
        subtitle="Perplexity searches for real SBA 7(a) lenders, banks, SBICs, mezzanine & private-credit funds. Save adds them to your lender shortlist with a source."
        kind="lenders" query={research.query} setQuery={research.setQuery} location={research.location} setLocation={research.setLocation}
        run={research.run} findMore={research.findMore} loading={research.loading} result={research.result} describe={describeLender} onSave={saveLender} />
      <AddToCampaignModal open={outreach.open} onClose={() => outreach.setOpen(false)} contacts={outreach.contacts} defaultGroupName={outreach.defaultName} source="lenders" />
      <SendToFolkModal open={folkOpen} onClose={() => setFolkOpen(false)} source="lenders" label="Debt Lenders" ids={crm.filter((l) => crmSel[l.id]).map((l) => l.id)} dealName={M.dealName || ""} />
      <ComposeEmailModal open={compose.open} onClose={() => compose.setOpen(false)} config={compose.config} />
    </div>
  );
}
